<template>
  <div class="relative overflow-hidden w-full">
    <div
      class="flex carousel"
      name="carousel-img"
      :style="{
        transform: `translateX(${carouselTranslateX}px)`,
        transition: carouselTransitionStyle,
      }"
      @touchstart="holdTouchStartPos"
      @touchmove="setCarouselSlideDistance"
      @touchend="judgeChangePage"
    >
      <div
        v-for="(image, index) in images"
        :key="index"
        class="min-w-full bg-black"
      >
        <img
          :src="image"
          class="w-full object-contain"
          :style="{height: `${carouselHeight}px`}"
          @error="setNoImage"
        />
      </div>
    </div>
    <!-- ページ番号(2枚以上の場合に表示) -->
    <div
      v-show="displayPageCount && 2 <= images?.length"
      name="carousel-pages"
      class="absolute top-2 right-2 bg-gray600 text-white rounded-[37px] w-[44px]"
    >
      {{ `${currentPageIndex + 1}/${images?.length}` }}
    </div>
    <!-- ナビゲーター(2枚以上画像がある場合) -->
    <div v-if="displayNavigator && 2 <= images?.length" class="flex mx-2 mt-2">
      <div
        v-for="(image, index) in images"
        :key="index"
        class="flex justify-center h-[60px] mx-1 first:ml-0 last:mr-0 relative"
        :style="{width: `${calcNavigateImgWidth}%`}"
      >
        <img
          name="carousel-navi-img"
          :src="image"
          class="w-full object-cover"
          @click="movePage(index)"
          @error="setNoImage"
        />
        <!-- 選択中の枠線 -->
        <div
          v-show="index == currentPageIndex"
          class="absolute w-full h-full border-solid border-[2px] border-primary"
        />
      </div>
    </div>
  </div>
</template>

<script>
/** スライド判定する際の閾値(横幅に対して何分割か) */
const THRESHOLD_SLIDE = 4
/** タッチイベントの対象index */
const TOUCH_INDEX = 0

const BaseCarouselForImage = {
  name: 'BaseCarouselForImage',
  data() {
    return {
      currentPageIndex: 0, // 現在のページindex
      touchStartPosX: 0, // タップ開始位置(x座標)
      currentTouchPosX: 0, // タップ中位置(x座標)
      carouselTranslateX: 0, // カルーセルの横方向スライド距離
      carouselTransitionStyle: 'none', // カルーセルのトランジション設定
    }
  },
  props: {
    /** 表示させる画像一覧 */
    images: {
      type: Array,
      required: true,
    },
    /** ページ番号を表示するか */
    displayPageCount: {
      type: Boolean,
      default: false,
    },
    /** ナビゲーターを表示するか */
    displayNavigator: {
      type: Boolean,
      default: false,
    },
    /** カルーセルの高さ */
    carouselHeight: {
      type: Number,
      default: 375,
    },
  },
  computed: {
    /**
     * ナビゲート画像の横幅算出(%値)
     */
    calcNavigateImgWidth: {
      get() {
        return (1 / this.images?.length) * 100 // eslint-disable-line no-magic-numbers
      },
    },
  },
  methods: {
    /**
     * 初期化処理
     */
    init() {
      this.currentPageIndex = 0
      this.touchStartPosX = 0
      this.currentTouchPosX = 0
      this.carouselTranslateX = 0
      this.carouselTransitionStyle = 'none'
    },
    /**
     * 表示失敗時処理
     * 読み込み失敗した場合、NoImageを表示する(全体表示とするため、object-fit:coverを適用)
     * @param {Event} event イベント情報
     */
    setNoImage(event) {
      event.target.style['object-fit'] = 'cover'
      event.target.src = require('@/assets/NoImage.svg')
    },
    /**
     * タッチ開始位置の保持
     * @param {TouchEvent} event イベント
     */
    holdTouchStartPos(event) {
      // 画像が一枚しかない場合はスライドさせない
      if (this.images.length === 1) return // eslint-disable-line no-magic-numbers

      this.touchStartPosX = event.touches[TOUCH_INDEX].clientX
      // トランジションを無効化にしておく
      this.carouselTransitionStyle = 'none'
    },
    /**
     * 横方向の移動距離を用いて、カルーセル位置(px)を算出し更新
     * @param {MoveEvent} event イベント
     */
    setCarouselSlideDistance(event) {
      // 画像が一枚しかない場合はスライドさせない
      if (this.images.length === 1) return // eslint-disable-line no-magic-numbers

      // スワイプの移動距離を算出
      this.currentTouchPosX = event.touches[TOUCH_INDEX].clientX
      const diffX = this.currentTouchPosX - this.touchStartPosX
      // スワイプの移動距離を元に、カルーセルの位置を更新
      this.carouselTranslateX =
        -this.currentPageIndex * this.$el.clientWidth + diffX
    },
    /**
     * ページ切り替え判定
     */
    judgeChangePage() {
      // 画像が一枚しかない場合はスライドさせない
      if (this.images.length === 1) return // eslint-disable-line no-magic-numbers

      const imageSwitchThreshold = this.$el.clientWidth / THRESHOLD_SLIDE
      const swipeDistance = this.currentTouchPosX - this.touchStartPosX

      if (swipeDistance > imageSwitchThreshold) {
        // 一定以上左にスライドしたら、前ページに切り替え
        this.prev()
      } else if (swipeDistance < -imageSwitchThreshold) {
        // 一定以上右にスライドしたら、次ページに切り替え
        this.next()
      } else {
        // 一定以上左右にスライドされていなければ、元のページに戻す
        this.snapBack()
      }
    },
    /**
     * 次ページに切り替え ※最終ページの場合、ページはそのまま
     */
    next() {
      // eslint-disable-next-line no-magic-numbers
      const lastPageIndex = this.images.length - 1
      if (this.currentPageIndex < lastPageIndex) {
        this.currentPageIndex++
      }
      this.snapBack()
    },
    /**
     * 前ページに切り替え ※先頭ページの場合、ページはそのまま
     */
    prev() {
      const topPageIndex = 0
      if (topPageIndex < this.currentPageIndex) {
        this.currentPageIndex--
      }
      this.snapBack()
    },
    /**
     * 保持しているページ番号に、カルーセル位置をアニメーション付きで移動
     */
    snapBack() {
      // トランジションを有効にし、カルーセル位置を更新
      this.carouselTransitionStyle = 'transform 0.5s ease'
      this.carouselTranslateX = -this.currentPageIndex * this.$el.clientWidth
    },

    /**
     * 指定ページにカルーセル画像を移動
     * @param {Number} index ページ番号
     */
    movePage(index) {
      this.currentPageIndex = index
      this.snapBack()
    },
  },
}
export default BaseCarouselForImage
</script>

<style scoped></style>
